import React, { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
// auth
import { AuthGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';
import Box from '@mui/material/Box';
import { SiteProvider } from '../../components/site/context/site-provider';
import SiteView from '../../components/site/site-view';
import { PostProvider } from '../../components/site/post/context';
import PostView from '../../components/site/post/post-view';
import PageView from '../../components/site/page/page-view';
import MultiChatbot from '../../cms-components/chatbot/multi-chatbot';
import ConfigChatComponent from '../../cms-components/chatbot/config-chat-component';
import TempConfigButtons from '../../components/temp-config-buttons/TempConfigButtons';

// ----------------------------------------------------------------------

// OVERVIEW
const IndexPage = lazy(() => import('src/pages/dashboard/app'));
// PRODUCT
const ProductDetailsPage = lazy(() => import('src/pages/dashboard/product/details'));
const ProductListPage = lazy(() => import('src/pages/dashboard/product/list'));
const ProductCreatePage = lazy(() => import('src/pages/dashboard/product/new'));
const ProductEditPage = lazy(() => import('src/pages/dashboard/product/edit'));
// ORDER
const OrderListPage = lazy(() => import('src/pages/dashboard/order/list'));
const OrderDetailsPage = lazy(() => import('src/pages/dashboard/order/details'));
// INVOICE
const InvoiceListPage = lazy(() => import('src/pages/dashboard/invoice/list'));
const InvoiceDetailsPage = lazy(() => import('src/pages/dashboard/invoice/details'));
const InvoiceCreatePage = lazy(() => import('src/pages/dashboard/invoice/new'));
const InvoiceEditPage = lazy(() => import('src/pages/dashboard/invoice/edit'));
// USER
const UserEditPage = lazy(() => import('src/pages/dashboard/user/edit'));
const UserTwoFactorPage = lazy(() => import('src/pages/dashboard/user/two-factor'));
// BLOG
const BlogPostsPage = lazy(() => import('src/pages/dashboard/post/list'));
const BlogPostPage = lazy(() => import('src/pages/dashboard/post/details'));
const BlogNewPostPage = lazy(() => import('src/pages/dashboard/post/new'));
const BlogEditPostPage = lazy(() => import('src/pages/dashboard/post/edit'));
// CMS POST
const PostsPage = lazy(() => import('src/pages/dashboard/posts/list'));
const PostsNewPage = lazy(() => import('src/pages/dashboard/posts/new'));
const PostsEditPage = lazy(() => import('src/pages/dashboard/posts/edit'));
const PostsCategoryPage = lazy(() => import('src/pages/dashboard/posts/category/list'));
const PostsCategoryCreatePage = lazy(() => import('src/pages/dashboard/posts/category/new'));
const PostsCategoryAddSonCreatePage = lazy(() => import('src/pages/dashboard/posts/category/new-son'));
const PostsCategoryEditPage = lazy(() => import('src/pages/dashboard/posts/category/edit'));
// CMS PAGE
const PageEditPage = lazy(() => import('src/pages/dashboard/page/edit'));
const PageAddPage = lazy(() => import('src/pages/dashboard/page/add'));
const PagePostAddPage = lazy(() => import('src/pages/dashboard/page/post-add'));
// CMS SITE
const SiteEditPage = lazy(() => import('src/pages/dashboard/site/edit'));
// CMS POST TYPE
const PostTypeEditPage = lazy(() => import('src/pages/dashboard/post-type/edit'));
const PostTypeAddPage = lazy(() => import('src/pages/dashboard/post-type/add'));
// JOB
const JobDetailsPage = lazy(() => import('src/pages/dashboard/job/details'));
const JobListPage = lazy(() => import('src/pages/dashboard/job/list'));
const JobCreatePage = lazy(() => import('src/pages/dashboard/job/new'));
const JobEditPage = lazy(() => import('src/pages/dashboard/job/edit'));
// TOUR
const TourDetailsPage = lazy(() => import('src/pages/dashboard/tour/details'));
const TourListPage = lazy(() => import('src/pages/dashboard/tour/list'));
const TourCreatePage = lazy(() => import('src/pages/dashboard/tour/new'));
const TourEditPage = lazy(() => import('src/pages/dashboard/tour/edit'));
// FILE MANAGER
const FileManagerPage = lazy(() => import('src/pages/dashboard/file-manager'));
const StorageManagerPage = lazy(() => import('src/pages/dashboard/storage-manager'));
// APP
const ChatPage = lazy(() => import('src/pages/dashboard/chat'));
const MailPage = lazy(() => import('src/pages/dashboard/mail'));
const CalendarPage = lazy(() => import('src/pages/dashboard/calendar'));
const KanbanPage = lazy(() => import('src/pages/dashboard/kanban'));
// TEST RENDER PAGE BY ROLE
const PermissionDeniedPage = lazy(() => import('src/pages/dashboard/permission'));
// BLANK PAGE
const BlankPage = lazy(() => import('src/pages/dashboard/blank'));

// CONFIG
const ConfigListPage = lazy(() => import('src/pages/dashboard/config/list'));
const ConfigSitePage = lazy(() => import('src/pages/dashboard/config/site/site'));
const ConfigSiteLayoutPage = lazy(() => import('src/pages/dashboard/config/site/site-layout'));
const ConfigSiteLayoutListPage = lazy(() => import('src/pages/dashboard/config/site/site-layout-list'));
const ConfigSitePreviewEditPage = lazy(() => import('src/pages/dashboard/config/site/site-preview-edit'));
const ConfigPostsOrderPage = lazy(() => import('src/pages/dashboard/config/posts-order/posts-order'));
const ConfigPageOrderPage = lazy(() => import('src/pages/dashboard/config/page-order/page-order'));
const ConfigPostTypeOrderPage = lazy(() => import('src/pages/dashboard/config/post-type-order/post-type-order'));

const ConfigPostLayoutPage = lazy(() => import('src/pages/dashboard/config/post/post-layout'));
const ConfigPostLayoutListPage = lazy(() => import('src/pages/dashboard/config/post/post-layout-list'));
const ConfigPostPage = lazy(() => import('src/pages/dashboard/config/post/post'));

const ConfigPostCategoryLayoutPage = lazy(() => import('src/pages/dashboard/config/post-category/post-category-layout'));
const ConfigPostCategoryLayoutListPage = lazy(() => import('src/pages/dashboard/config/post-category/post-category-layout-list'));
const ConfigPostCategoryPage = lazy(() => import('src/pages/dashboard/config/post-category/post-category'));
const ConfigPostCategoryPreviewEditPage = lazy(() => import('src/pages/dashboard/config/post-category/post-category-preview-edit'));

const ConfigPagePage = lazy(() => import('src/pages/dashboard/config/page/page'));
const ConfigPageLayoutPage = lazy(() => import('src/pages/dashboard/config/page/page-layout'));
const ConfigPageLayoutListPage = lazy(() => import('src/pages/dashboard/config/page/page-layout-list'));
const ConfigPagePreviewEditPage = lazy(() => import('src/pages/dashboard/config/page/page-preview-edit'));
const ConfigPostTypePage = lazy(() => import('src/pages/dashboard/config/post-type/post-type'));
const ConfigPostTypeLayoutPage = lazy(() => import('src/pages/dashboard/config/post-type/post-type-layout'));
const ConfigPostTypeLayoutListPage = lazy(() => import('src/pages/dashboard/config/post-type/post-type-layout-list'));
const ConfigPostTypePreviewEditPage = lazy(() => import('src/pages/dashboard/config/post-type/post-type-preview-edit'));
const ConfigPostPreviewEditPage = lazy(() => import('src/pages/dashboard/config/post/post-preview-edit'));
const ConfigPostEditPreviewPage = lazy(() => import('src/pages/dashboard/config/post/post-edit-preview'));
const ConfigPostSummaryEditPage = lazy(() => import('src/pages/dashboard/config/post/post-summary-edit'));
const ConfigAiSettingsPage = lazy(() => import('src/pages/dashboard/config/ai-settings'));
const QAGenerationPage = lazy(() => import('src/pages/dashboard/qa-generation'));
const QAGenerationTaskPage = lazy(() => import('src/pages/dashboard/qa-generation-task'));
const QAGenerationTaskDetailsPage = lazy(() => import('src/pages/dashboard/qa-generation-task-details'));
// CUSTOMER LOG
const CustomerLogListPage = lazy(() => import('src/pages/dashboard/customer-log'));

// V2
const V2ApiConfigPage = lazy(() => import('src/pages/dashboard/v2/site/api-config'));
const V2SiteLayoutListPage = lazy(() => import('src/pages/dashboard/v2/site/site-layout-list'));
const V2SitePreviewEditPage = lazy(() => import('src/pages/dashboard/v2/site/site-preview-edit'));
const V2SitePage = lazy(() => import('src/pages/dashboard/v2/site/site'));
const V2AiSettingsPage = lazy(() => import('src/pages/dashboard/v2/ai/ai-settings'));
const V2SiteEditPage = lazy(() => import('src/pages/dashboard/v2/site/site-edit'));
const V2PostTypeAddPage = lazy(() => import('src/pages/dashboard/v2/post-type/post-type-add'));
const V2PageAddPage = lazy(() => import('src/pages/dashboard/v2/site/page-add'));
const V2PagePostAddPage = lazy(() => import('src/pages/dashboard/v2/site/page-post-add'));
const V2PostTypeListPage = lazy(() => import('src/pages/dashboard/v2/post-type/post-type-list'));
const V2PostTypeLayoutListPage = lazy(() => import('src/pages/dashboard/v2/post-type/post-type-layout-list'));
const V2PostTypeSitePage = lazy(() => import('src/pages/dashboard/v2/post-type/post-type-site'));
const V2PostTypePreviewPage = lazy(() => import('src/pages/dashboard/v2/post-type/post-type-preview'));
const V2PostTypeEditPage = lazy(() => import('src/pages/dashboard/v2/post-type/post-type-edit'));
const V2PostConfigListPage = lazy(() => import('src/pages/dashboard/v2/post/post-list'));
const V2PostLayoutListPage = lazy(() => import('src/pages/dashboard/v2/post/post-layout-list'));
const V2PostSitePage = lazy(() => import('src/pages/dashboard/v2/post/post-site'));
const V2PostConfigPreviewPage = lazy(() => import('src/pages/dashboard/v2/post/post-config-preview'));
const V2PostPreviewPage = lazy(() => import('src/pages/dashboard/v2/post/post-preview'));
const V2PostSummaryEditPage = lazy(() => import('src/pages/dashboard/v2/post/post-summary-edit'));
const V2PostCategoryConfigListPage = lazy(() => import('src/pages/dashboard/v2/post-category/post-category-list'));
const V2PostCategoryLayoutListPage = lazy(() => import('src/pages/dashboard/v2/post-category/post-category-layout-list'));
const V2PostCategorySitePage = lazy(() => import('src/pages/dashboard/v2/post-category/post-category-site'));
const V2PostCategoryConfigPreviewPage = lazy(() => import('src/pages/dashboard/v2/post-category/post-category-config-preview'));

const V2ThemeListPage = lazy(() => import('src/pages/dashboard/v2/theme/theme-list'));
const V2ThemeCreatePage = lazy(() => import('src/pages/dashboard/v2/theme/theme-new'));
const V2ThemeEditPage = lazy(() => import('src/pages/dashboard/v2/theme/theme-edit'));

const V2TemplatePkgListPage = lazy(() => import('src/pages/dashboard/v2/template-pkg/template-pkg-list'));
const V2TemplatePkgCreatePage = lazy(() => import('src/pages/dashboard/v2/template-pkg/template-pkg-new'));
const V2TemplatePkgEditPage = lazy(() => import('src/pages/dashboard/v2/template-pkg/template-pkg-edit'));

const V2PagePkgListPage = lazy(() => import('src/pages/dashboard/v2/page-pkg/page-pkg-list'));
const V2PagePkgCreatePage = lazy(() => import('src/pages/dashboard/v2/page-pkg/page-pkg-new'));
const V2PagePkgEditPage = lazy(() => import('src/pages/dashboard/v2/page-pkg/page-pkg-edit'));

const V2ThemeTemplateListPage = lazy(() => import('src/pages/dashboard/v2/theme-template/template-list'));
const V2ThemeTemplateCreatePage = lazy(() => import('src/pages/dashboard/v2/theme-template/template-new'));
const V2ThemeTemplateEditPage = lazy(() => import('src/pages/dashboard/v2/theme-template/template-edit'));
const V2ThemeLayoutListPage = lazy(() => import('src/pages/dashboard/v2/theme-template/layout-list'));
const V2ThemeLayoutCreatePage = lazy(() => import('src/pages/dashboard/v2/theme-template/layout-new'));
const V2ThemeLayoutEditPage = lazy(() => import('src/pages/dashboard/v2/theme-template/layout-edit'));
const V2ThemeBlockListPage = lazy(() => import('src/pages/dashboard/v2/theme-template/block-list'));
const V2ThemeBlockCreatePage = lazy(() => import('src/pages/dashboard/v2/theme-template/block-new'));
const V2ThemeBlockEditPage = lazy(() => import('src/pages/dashboard/v2/theme-template/block-edit'));

const V2ThemePageListPage = lazy(() => import('src/pages/dashboard/v2/theme-page/page-list'));
const V2ThemePageLayoutListPage = lazy(() => import('src/pages/dashboard/v2/theme-page/page-layout-list'));
const V2ThemePageItemPage = lazy(() => import('src/pages/dashboard/v2/theme-page/page-item'));
const V2ThemePagePreviewEditPage = lazy(() => import('src/pages/dashboard/v2/theme-page/page-preview-edit'));
const V2ThemePageAddPage = lazy(() => import('src/pages/dashboard/v2/theme-page/page-add'));
const V2ThemePagePostAddPage = lazy(() => import('src/pages/dashboard/v2/theme-page/page-post-add'));
const V2ThemePageEditPage = lazy(() => import('src/pages/dashboard/v2/theme-page/page-edit'));

const V2ThemeDesignEditorPage = lazy(() => import('src/pages/dashboard/v2/theme/theme-design-editor'));

// V2 FORM
const V2FormListPage = lazy(() => import('src/pages/dashboard/v2/form/list'));
const V2FormNewPage = lazy(() => import('src/pages/dashboard/v2/form/new'));
const V2FormEditPage = lazy(() => import('src/pages/dashboard/v2/form/edit'));
const V2FormItemsPage = lazy(() => import('src/pages/dashboard/v2/form/items'));
const V2FormMailPage = lazy(() => import('src/pages/dashboard/v2/form/mail'));
const V2FormDataPage = lazy(() => import('src/pages/dashboard/v2/form/data'));
const V2FormErrorMessagePage = lazy(() => import('src/pages/dashboard/v2/form/error-message'));
// V2 SITE CONFIG
const V2SiteConfigPage = lazy(() => import('src/pages/dashboard/v2/site/config'));


const TemplateListPage = lazy(() => import('src/sections/template/view/template-list-view'));
const TemplateCreatePage = lazy(() => import('src/sections/template/view/template-create-view'));
const TemplateEditPage = lazy(() => import('src/sections/template/view/template-edit-view'));
const LayoutListPage = lazy(() => import('src/sections/template/view/layout-list-view'));
const LayoutCreatePage = lazy(() => import('src/sections/template/view/layout-create-view'));
const LayoutEditPage = lazy(() => import('src/sections/template/view/layout-edit-view'));
const BlockListPage = lazy(() => import('src/sections/template/view/block-list-view'));
const BlockCreatePage = lazy(() => import('src/sections/template/view/block-create-view'));
const BlockEditPage = lazy(() => import('src/sections/template/view/block-edit-view'));

// ----------------------------------------------------------------------

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <SiteProvider>
          <DashboardLayout>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
            <Box
              sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                zIndex: 1000,
              }}
            >
              <MultiChatbot type="simple" />
            </Box>
          </DashboardLayout>
        </SiteProvider>
      </AuthGuard>
    ),
    children: [
      { element: <IndexPage />, index: true },
      {
        path: 'user',
        children: [
          { path: 'edit', element: <UserEditPage />, index: true },
          { path: 'two-factor', element: <UserTwoFactorPage /> },
        ],
      },
      {
        path: 'product',
        element: (
          <SiteView>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </SiteView>
        ),
        children: [
          { element: <ProductListPage />, index: true },
          { path: 'list', element: <ProductListPage /> },
          { path: ':id', element: <ProductDetailsPage /> },
          { path: 'new', element: <ProductCreatePage /> },
          { path: ':id/edit', element: <ProductEditPage /> },
        ],
      },
      {
        path: 'order',
        element: (
          <SiteView>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </SiteView>
        ),
        children: [
          { element: <OrderListPage />, index: true },
          { path: 'list', element: <OrderListPage /> },
          { path: ':id', element: <OrderDetailsPage /> },
        ],
      },
      {
        path: 'invoice',
        element: (
          <SiteView>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </SiteView>
        ),
        children: [
          { element: <InvoiceListPage />, index: true },
          { path: 'list', element: <InvoiceListPage /> },
          { path: ':id', element: <InvoiceDetailsPage /> },
          { path: ':id/edit', element: <InvoiceEditPage /> },
          { path: 'new', element: <InvoiceCreatePage /> },
        ],
      },
      {
        path: 'post',
        element: (
          <SiteView>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </SiteView>
        ),
        children: [
          { element: <BlogPostsPage />, index: true },
          { path: 'list', element: <BlogPostsPage /> },
          { path: ':title', element: <BlogPostPage /> },
          { path: ':title/edit', element: <BlogEditPostPage /> },
          { path: 'new', element: <BlogNewPostPage /> },
        ],
      },

      {
        path: 'site/:site_code',
        element: (
          <SiteView>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </SiteView>
        ),
        children: [
          { element: <IndexPage />, index: true },
          {
            path: 'posts',
            element: (
              <SiteView>
                <PostProvider>
                  <PostView>
                    <Suspense fallback={<LoadingScreen />}>
                      <Outlet />
                    </Suspense>
                  </PostView>
                </PostProvider>
              </SiteView>
            ),
            children: [
              { path: ':post_type_id', element: <PostsPage />, index: true },
              { path: ':post_type_id/list', element: <PostsPage /> },
              { path: ':post_type_id/new', element: <PostsNewPage /> },
              { path: ':post_type_id/:id/edit', element: <PostsEditPage /> },
              { path: ':post_type_id/category/list', element: <PostsCategoryPage /> },
              { path: ':post_type_id/category/new', element: <PostsCategoryCreatePage /> },
              { path: ':post_type_id/category/:id/new-son', element: <PostsCategoryAddSonCreatePage /> },
              { path: ':post_type_id/category/:id/edit', element: <PostsCategoryEditPage /> },
            ],
          },
          {
            path: 'config',
            element: (
              <SiteView>
                <Suspense fallback={<LoadingScreen />}>

                  <TempConfigButtons />
                  <ConfigChatComponent />

                  <Outlet />
                </Suspense>
              </SiteView>
            ),
            children: [
              { path: 'list', element: <ConfigListPage />, index: true },
              { path: 'site/:name', element: <ConfigSitePage /> },
              { path: 'site/layout', element: <ConfigSiteLayoutPage /> },
              { path: 'site/layout/list', element: <ConfigSiteLayoutListPage /> },
              { path: 'site/preview-edit', element: <ConfigSitePreviewEditPage /> },
              { path: 'posts-order', element: <ConfigPostsOrderPage /> },
              { path: 'page-order', element: <ConfigPageOrderPage /> },
              { path: 'post-type-order', element: <ConfigPostTypeOrderPage /> },

              { path: 'post/:post_type_id/layout', element: <ConfigPostLayoutPage /> },
              { path: 'post/:post_type_id/layout/list', element: <ConfigPostLayoutListPage /> },
              { path: 'post/:post_type_id/preview-edit', element: <ConfigPostPreviewEditPage /> },
              { path: 'post/:post_type_id/edit-preview', element: <ConfigPostEditPreviewPage /> },
              { path: 'post/:post_type_id/summary-edit', element: <ConfigPostSummaryEditPage /> },
              { path: 'post/:post_type_id/:name', element: <ConfigPostPage /> },

              { path: 'post-category/:post_type_id/layout', element: <ConfigPostCategoryLayoutPage /> },
              { path: 'post-category/:post_type_id/layout/list', element: <ConfigPostCategoryLayoutListPage /> },
              { path: 'post-category/:post_type_id/preview-edit', element: <ConfigPostCategoryPreviewEditPage /> },
              { path: 'post-category/:post_type_id/:name', element: <ConfigPostCategoryPage /> },

              { path: 'page/:page_pkg_code/:page_id/:name', element: <ConfigPagePage /> },
              { path: 'page/:page_pkg_code/:page_id/layout', element: <ConfigPageLayoutPage /> },
              { path: 'page/:page_pkg_code/:page_id/layout/list', element: <ConfigPageLayoutListPage /> },
              { path: 'page/:page_pkg_code/:page_id/preview-edit', element: <ConfigPagePreviewEditPage /> },
              { path: 'post-type/:post_type_id/:name', element: <ConfigPostTypePage /> },
              { path: 'post-type/:post_type_id/layout', element: <ConfigPostTypeLayoutPage /> },
              { path: 'post-type/:post_type_id/layout/list', element: <ConfigPostTypeLayoutListPage /> },
              { path: 'post-type/:post_type_id/preview-edit', element: <ConfigPostTypePreviewEditPage /> },

              { path: 'post-type-add', element: <PostTypeAddPage /> },
              { path: 'page-add', element: <PageAddPage /> },
              { path: 'page-post-add', element: <PagePostAddPage /> },
              { path: 'ai-settings', element: <ConfigAiSettingsPage /> },
            ],
          },
          {
            path: 'page',
            element: (
              <SiteView>
                <PageView>
                  <Suspense fallback={<LoadingScreen />}>
                    <Outlet />
                  </Suspense>
                </PageView>
              </SiteView>
            ),
            children: [
              { path: ':id/edit', element: <PageEditPage /> },
            ],
          },
          {
            path: 'edit',
            element: (
              <SiteView>
                <Suspense fallback={<LoadingScreen />}>
                  <SiteEditPage />
                </Suspense>
              </SiteView>
            ),
          },
          {
            path: 'post-type',
            element: (
              <SiteView>
                <Suspense fallback={<LoadingScreen />}>
                  <Outlet />
                </Suspense>
              </SiteView>
            ),
            children: [
              { path: ':id/edit', element: <PostTypeEditPage /> },
            ],
          },
          {
            path: 'storage-manager', element:
              <SiteView>
                <StorageManagerPage />
              </SiteView>
          },
          {
            path: 'customer-log', element:
              <SiteView>
                <CustomerLogListPage />
              </SiteView>
          },
          {
            path: 'qa-generation', element:
              <SiteView>
                <QAGenerationPage />
              </SiteView>
          },
          {
            path: 'qa-generation-task', element:
              <SiteView>
                <QAGenerationTaskPage />
              </SiteView>
          },
          {
            path: 'qa-generation-task/:taskId', element:
              <SiteView>
                <QAGenerationTaskDetailsPage />
              </SiteView>
          },
          {
            path: 'v2',
            element: (
              <SiteView>
                <Suspense fallback={<LoadingScreen />}>
                  <Outlet />
                </Suspense>
              </SiteView>
            ),
            children: [
              { path: 'api-config', element: <V2ApiConfigPage /> },
              { path: 'site/layout/list', element: <V2SiteLayoutListPage /> },
              { path: 'site/preview-edit', element: <V2SitePreviewEditPage /> },
              { path: 'site/:name', element: <V2SitePage /> },
              { path: 'edit', element: <V2SiteEditPage /> },
              { path: 'ai-settings', element: <V2AiSettingsPage /> },
              { path: 'post-type-add', element: <V2PostTypeAddPage /> },
              { path: 'page-add', element: <V2PageAddPage /> },
              { path: 'page-post-add', element: <V2PagePostAddPage /> },
              { path: 'post-type-list', element: <V2PostTypeListPage /> },
              { path: 'post-type/:post_type_id/layout/list', element: <V2PostTypeLayoutListPage /> },
              { path: 'post-type/:post_type_id/preview-edit', element: <V2PostTypePreviewPage /> },
              { path: 'post-type/:post_type_id/edit', element: <V2PostTypeEditPage /> },
              { path: 'post-type/:post_type_id/:name', element: <V2PostTypeSitePage /> },
              { path: 'post-config-list', element: <V2PostConfigListPage /> },
              { path: 'post/:post_type_id/layout/list', element: <V2PostLayoutListPage /> },
              { path: 'post/:post_type_id/preview-edit', element: <V2PostConfigPreviewPage /> },
              { path: 'post/:post_type_id/edit-preview', element: <V2PostPreviewPage /> },
              { path: 'post/:post_type_id/summary-edit', element: <V2PostSummaryEditPage /> },
              { path: 'post/:post_type_id/:name', element: <V2PostSitePage /> },
              { path: 'post-category-config-list', element: <V2PostCategoryConfigListPage /> },
              { path: 'post-category/:post_type_id/layout/list', element: <V2PostCategoryLayoutListPage /> },
              { path: 'post-category/:post_type_id/preview-edit', element: <V2PostCategoryConfigPreviewPage /> },
              { path: 'post-category/:post_type_id/:name', element: <V2PostCategorySitePage /> },

              { path: 'theme-list', element: <V2ThemeListPage /> },
              { path: 'theme/new', element: <V2ThemeCreatePage /> },
              { path: 'theme/:theme_code/edit', element: <V2ThemeEditPage /> },

              { path: 'template-pkg-list', element: <V2TemplatePkgListPage /> },
              { path: 'template-pkg/new', element: <V2TemplatePkgCreatePage /> },
              { path: 'template-pkg/:code/edit', element: <V2TemplatePkgEditPage /> },

              { path: 'page-pkg-list', element: <V2PagePkgListPage /> },
              { path: 'page-pkg/new', element: <V2PagePkgCreatePage /> },
              { path: 'page-pkg/:code/edit', element: <V2PagePkgEditPage /> },

              { path: 'theme/:theme_code/template/list', element: <V2ThemeTemplateListPage /> },
              { path: 'theme/:theme_code/template/new', element: <V2ThemeTemplateCreatePage /> },
              { path: 'theme/:theme_code/template/:tpl_code/edit', element: <V2ThemeTemplateEditPage /> },
              { path: 'theme/:theme_code/layout/list', element: <V2ThemeLayoutListPage /> },
              { path: 'theme/:theme_code/layout/new', element: <V2ThemeLayoutCreatePage /> },
              { path: 'theme/:theme_code/layout/:layout_code/edit', element: <V2ThemeLayoutEditPage /> },
              { path: 'theme/:theme_code/block/list', element: <V2ThemeBlockListPage /> },
              { path: 'theme/:theme_code/block/new', element: <V2ThemeBlockCreatePage /> },
              { path: 'theme/:theme_code/block/:block_code/edit', element: <V2ThemeBlockEditPage /> },

              { path: 'theme/:theme_code/page/list', element: <V2ThemePageListPage /> },
              { path: 'theme/:theme_code/page/add', element: <V2ThemePageAddPage /> },
              { path: 'theme/:theme_code/page/post-add', element: <V2ThemePagePostAddPage /> },
              { path: 'theme/:theme_code/page/:page_id/layout/list', element: <V2ThemePageLayoutListPage /> },
              { path: 'theme/:theme_code/page/:page_id/preview-edit', element: <V2ThemePagePreviewEditPage /> },
              { path: 'theme/:theme_code/page/:page_id/edit', element: <V2ThemePageEditPage /> },
              { path: 'theme/:theme_code/page/:page_id/:name', element: <V2ThemePageItemPage /> },

              { path: 'theme/:theme_code/design-editor', element: <V2ThemeDesignEditorPage /> },

              // フォーム管理
              { path: 'form', element: <V2FormListPage /> },
              { path: 'form/new', element: <V2FormNewPage /> },
              { path: 'form/error-message', element: <V2FormErrorMessagePage /> },
              { path: 'form/:form_id/edit', element: <V2FormEditPage /> },
              { path: 'form/:form_id/items', element: <V2FormItemsPage /> },
              { path: 'form/:form_id/mail', element: <V2FormMailPage /> },
              { path: 'form/:form_id/data', element: <V2FormDataPage /> },
              // サイト環境設定
              { path: 'site-config', element: <V2SiteConfigPage /> },
            ],
          },
          {
            path: 'template',
            element: (
              <SiteView>
                <Suspense fallback={<LoadingScreen />}>
                  <Outlet />
                </Suspense>
              </SiteView>
            ),
            children: [
              { path: 'list', element: <TemplateListPage /> },
              { path: 'new', element: <TemplateCreatePage /> },
              { path: ':tpl_code/edit', element: <TemplateEditPage /> },
              { path: 'layout/list', element: <LayoutListPage /> },
              { path: 'layout/new', element: <LayoutCreatePage /> },
              { path: 'layout/:layout_code/edit', element: <LayoutEditPage /> },
              { path: 'block/list', element: <BlockListPage /> },
              { path: 'block/new', element: <BlockCreatePage /> },
              { path: 'block/:block_code/edit', element: <BlockEditPage /> },
            ],
          },
        ],
      },



      {
        path: 'job',
        element: (
          <SiteView>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </SiteView>
        ),
        children: [
          { element: <JobListPage />, index: true },
          { path: 'list', element: <JobListPage /> },
          { path: ':id', element: <JobDetailsPage /> },
          { path: 'new', element: <JobCreatePage /> },
          { path: ':id/edit', element: <JobEditPage /> },
        ],
      },
      {
        path: 'tour',
        element: (
          <SiteView>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </SiteView>
        ),
        children: [
          { element: <TourListPage />, index: true },
          { path: 'list', element: <TourListPage /> },
          { path: ':id', element: <TourDetailsPage /> },
          { path: 'new', element: <TourCreatePage /> },
          { path: ':id/edit', element: <TourEditPage /> },
        ],
      },
      {
        path: 'file-manager', element:
          <SiteView>
            <FileManagerPage />
          </SiteView>
      },
      { path: 'mail', element: <MailPage /> },
      { path: 'chat', element: <ChatPage /> },
      { path: 'calendar', element: <CalendarPage /> },
      { path: 'kanban', element: <KanbanPage /> },
      { path: 'permission', element: <PermissionDeniedPage /> },
      { path: 'blank', element: <BlankPage /> },
    ],
  },
];
